/**
 * Plugins in the app (ADR 0025): loads each enabled plugin into its own sandbox
 * once, wires its `nexus.*` calls to the host (permissions, confirmations,
 * network proxy), and keeps a short log per plugin.
 */
import { create } from 'zustand';
import type { ActionPreview } from '@nexus/ai';
import { createHostApi, listPlugins, type HttpRequest, type InstalledPlugin } from '@nexus/plugins';
import type { PluginSandbox } from '@nexus/plugins/sandbox';
import type { NotificationRecord, Store } from '@nexus/store';
import { useStoreQuery } from '../db/context';
import { useToasts } from '../notifications/toasts';
import { supabase } from '../sync/useSync';

// ---- confirmations ---------------------------------------------------------------------

interface Pending {
  id: number;
  plugin: string;
  preview: ActionPreview;
  resolve: (ok: boolean) => void;
}
let nextId = 1;

/** Changes plugins asked for, waiting for the user's decision. */
export const usePluginConfirm = create<{
  queue: Pending[];
  ask: (plugin: string, preview: ActionPreview) => Promise<boolean>;
  answer: (id: number, ok: boolean) => void;
}>((set, get) => ({
  queue: [],
  ask: (plugin, preview) =>
    new Promise<boolean>((resolve) =>
      set((s) => ({ queue: [...s.queue, { id: nextId++, plugin, preview, resolve }] })),
    ),
  answer: (id, ok) => {
    get()
      .queue.find((p) => p.id === id)
      ?.resolve(ok);
    set((s) => ({ queue: s.queue.filter((p) => p.id !== id) }));
  },
}));

// ---- log -------------------------------------------------------------------------------

export const usePluginLog = create<{
  lines: Record<string, string[]>;
  add: (id: string, line: string) => void;
}>((set) => ({
  lines: {},
  add: (id, line) =>
    set((s) => ({
      lines: {
        ...s.lines,
        [id]: [...(s.lines[id] ?? []), `${new Date().toLocaleTimeString()} ${line}`].slice(-100),
      },
    })),
}));

// ---- sandboxes -------------------------------------------------------------------------

export type Describe = (p: ActionPreview) => string;

interface Loaded {
  version: number;
  box: Promise<PluginSandbox>;
}
const loaded = new Map<string, Loaded>();

/** HTTPS through the `plugin-fetch` function, so the app's own security rules stay tight. */
async function proxyFetch(req: HttpRequest, allowed: string[]) {
  const { data, error } = await supabase().functions.invoke('plugin-fetch', {
    body: { ...req, allowed },
  });
  if (error) throw new Error('Het internetverzoek is mislukt.');
  const res = data as { status?: number; text?: string; error?: string };
  if (res.error) throw new Error(res.error);
  return { status: res.status ?? 0, text: res.text ?? '' };
}

export function pluginToast(title: string, body: string) {
  const now = Date.now();
  const n: NotificationRecord = {
    id: `plugin-${now}-${Math.random()}`,
    key: 'plugin',
    kind: 'reminder',
    taskId: null,
    pageId: null,
    title,
    body,
    fireAt: now,
    createdAt: now,
    readAt: null,
  };
  useToasts.getState().push(n);
}

/** The plugin's sandbox (loaded once per version). Throws when the code does not run. */
export function sandboxFor(
  store: Store,
  plugin: InstalledPlugin,
  describe: Describe,
  opts: { dryRun?: boolean } = {},
) {
  const key = `${plugin.pageId}${opts.dryRun ? ':dry' : ''}`;
  const current = loaded.get(key);
  if (current && current.version === plugin.updatedAt) return current.box;
  void current?.box.then((b) => b.dispose()).catch(() => {});
  const name = plugin.manifest.name;
  const log = (line: string) => usePluginLog.getState().add(plugin.pageId, line);
  const host = createHostApi({
    store,
    manifest: plugin.manifest,
    pluginPageId: plugin.pageId,
    dryRun: opts.dryRun,
    confirm: (preview) => usePluginConfirm.getState().ask(name, preview),
    onApplied: async (preview, ran) => {
      await store.addAiActivity({
        action: preview.action,
        summary: `${name}: ${describe(preview)}`,
        pageId: ran.pageId,
        undo: ran.undo,
      });
    },
    fetch: (req) => proxyFetch(req, plugin.manifest.permissions.network),
    toast: (text) => pluginToast(name, text),
    log,
  });
  const box = import('@nexus/plugins/sandbox').then(({ PluginSandbox }) =>
    PluginSandbox.create(plugin.code, host),
  );
  box.catch((e: unknown) => log(`Fout bij laden: ${e instanceof Error ? e.message : String(e)}`));
  loaded.set(key, { version: plugin.updatedAt, box });
  return box;
}

/** Forget a plugin's sandbox (removed, switched off). */
export function unloadPlugin(pageId: string) {
  for (const [key, l] of loaded)
    if (key.startsWith(pageId)) {
      void l.box.then((b) => b.dispose()).catch(() => {});
      loaded.delete(key);
    }
}

/** Id of the hidden "Plugins" folder, if any. */
export function usePluginsFolderId(): string | null {
  return useStoreQuery((s) => s.getSetting('plugins_parent_id'), [], ['settings']) ?? null;
}

export function usePlugins(): InstalledPlugin[] | undefined {
  return useStoreQuery((s) => listPlugins(s), [], ['pages', 'props', 'settings']);
}
